import { useState } from 'react'
import { projectStatusReasonRequired, type ProjectStatus } from '@symphenia/shared-types'
import { fieldLabel, fieldWrapper, inputBase, textareaBase } from '@/lib/form-styles'
import { apiErrorText } from '@/lib/api-error'
import { changeProjectStatus } from '../api/projects-head.api'
import { PROJECT_STATUS_LABELS } from '../project-labels'
import type { ProjectDetail } from '../types/project.types'

/**
 * AP-35 Detailblatt 1 5.5 — **Statuswechsel mit Begründung.**
 *
 * Die Maske zeigt nur Ziele, die der Server erlaubt (`allowedTransitions`); der
 * Dialog fragt die Begründung dort, wo 3.2 sie verlangt, und bei
 * „fertiggestellt" die beiden Termine zur Bestätigung. Geprüft wird trotzdem
 * erneut auf dem Server — die Maske ist Bequemlichkeit, keine Regel.
 */
export function ProjectStatusDialog({
  projekt,
  ziel,
  onClose,
  onDone,
}: {
  projekt: ProjectDetail
  ziel: ProjectStatus
  onClose: () => void
  onDone: (p: ProjectDetail) => void
}) {
  const [note, setNote] = useState('')
  const [completedAt, setCompletedAt] = useState(new Date().toISOString().slice(0, 10))
  const [warrantyEndsAt, setWarrantyEndsAt] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const grundPflicht = projectStatusReasonRequired(projekt.status as ProjectStatus, ziel)
  const fertigstellung = ziel === 'COMPLETED'

  async function wechseln() {
    setLaeuft(true)
    setFehler(null)
    try {
      const neu = await changeProjectStatus(projekt.id, {
        to: ziel,
        note: note.trim() || undefined,
        completedAt: fertigstellung ? completedAt : undefined,
        warrantyEndsAt: fertigstellung && warrantyEndsAt ? warrantyEndsAt : undefined,
      })
      onDone(neu)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg space-y-4 rounded-lg border border-border bg-background p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-primary">
          Status ändern: {PROJECT_STATUS_LABELS[projekt.status as ProjectStatus]?.label} →{' '}
          {PROJECT_STATUS_LABELS[ziel]?.label}
        </h2>

        {fertigstellung && (
          <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            Alle offenen Positionen werden mit fertiggestellt, laufende Arbeitsschritte als fertig gemeldet.
          </p>
        )}

        {fertigstellung && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className={fieldWrapper}>
              <label className={fieldLabel}>fertiggestellt am</label>
              <input type="date" className={inputBase} value={completedAt} onChange={(e) => setCompletedAt(e.target.value)} />
            </div>
            <div className={fieldWrapper}>
              <label className={fieldLabel}>Gewährleistung bis</label>
              <input
                type="date"
                className={inputBase}
                value={warrantyEndsAt}
                placeholder="aus den Vorgaben"
                onChange={(e) => setWarrantyEndsAt(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">leer = aus den Vorgaben gerechnet</p>
            </div>
          </div>
        )}

        <div className={fieldWrapper}>
          <label className={fieldLabel}>
            Begründung {grundPflicht && <span className="text-destructive">*</span>}
          </label>
          <textarea className={textareaBase} rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>

        {fehler && <p className="text-sm text-destructive">{fehler}</p>}

        <div className="flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-4 py-2 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
            disabled={laeuft || (grundPflicht && note.trim().length === 0)}
            onClick={() => void wechseln()}
          >
            {laeuft ? 'Wechselt …' : 'Status ändern'}
          </button>
        </div>
      </div>
    </div>
  )
}
